import { useCallback } from "react";
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  OutlinedInput,
  Button,
  Typography,
  Stack,
} from "@mui/material";
import { Clear as ClearIcon } from "@mui/icons-material";
import { FilingType } from "@taxassist/api-client";
import {
  getUserFriendlyStatuses,
  type UserFriendlyStatus,
} from "../../utils/statusMapping";
import type { JobFilters } from "../../pages/jobs/ListJobsView";

const FILING_TYPE_LABELS: Record<FilingType, string> = {
  "1040": "Form 1040 (Individual)",
  "1041": "Form 1041 (Estate/Trust)",
  "1065": "Form 1065 (Partnership)",
  "1120": "Form 1120 (Corporation)",
  "1120S": "Form 1120S (S Corporation)",
};

interface AdvancedFiltersProps {
  filters: JobFilters;
  onFiltersChange: (filters: JobFilters) => void;
  onClearAll: () => void;
  jobsCount: number;
}

export function AdvancedFilters({
  filters,
  onFiltersChange,
  onClearAll,
  jobsCount,
}: AdvancedFiltersProps) {
  const handleStatusChange = useCallback(
    (statuses: UserFriendlyStatus[]) => {
      onFiltersChange({
        ...filters,
        userFriendlyStatuses: statuses.length > 0 ? statuses : undefined,
      });
    },
    [filters, onFiltersChange],
  );

  const handleFilingTypeChange = useCallback(
    (filingTypes: FilingType[]) => {
      onFiltersChange({
        ...filters,
        filingTypes: filingTypes.length > 0 ? filingTypes : undefined,
      });
    },
    [filters, onFiltersChange],
  );

  const hasActiveFilters = !!(
    filters.userFriendlyStatuses?.length || filters.filingTypes?.length
  );

  return (
    <Box>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={3}
        alignItems={{ xs: "stretch", md: "flex-end" }}
      >
        {/* Status Filter */}
        <Box sx={{ flex: "0 0 auto", minWidth: 200 }}>
          <Typography variant="subtitle2" gutterBottom fontWeight={600}>
            Status
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              multiple
              value={filters.userFriendlyStatuses || []}
              onChange={(e) =>
                handleStatusChange(e.target.value as UserFriendlyStatus[])
              }
              input={<OutlinedInput placeholder="All statuses" />}
              renderValue={(selected) => {
                if (selected.length === 0) return <em>All statuses</em>;
                return `${selected.length} selected`;
              }}
              displayEmpty
            >
              {getUserFriendlyStatuses().map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Filing Type Filter */}
        <Box sx={{ flex: "0 0 auto", minWidth: 200 }}>
          <Typography variant="subtitle2" gutterBottom fontWeight={600}>
            Filing Type
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              multiple
              value={filters.filingTypes || []}
              onChange={(e) =>
                handleFilingTypeChange(e.target.value as FilingType[])
              }
              input={<OutlinedInput placeholder="All types" />}
              renderValue={(selected) => {
                if (selected.length === 0) return <em>All types</em>;
                return `${selected.length} selected`;
              }}
              displayEmpty
            >
              {Object.entries(FILING_TYPE_LABELS).map(([type, label]) => (
                <MenuItem key={type} value={type}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Clear All Button */}
        <Box sx={{ flex: "0 0 auto" }}>
          <Button
            variant="outlined"
            onClick={onClearAll}
            disabled={!hasActiveFilters}
            startIcon={<ClearIcon />}
            size="small"
            sx={{ height: 40, mt: { xs: 0, md: 3 } }}
          >
            Clear All
          </Button>
        </Box>
      </Stack>

      {/* Results Count */}
      <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Showing
        </Typography>
        <Typography variant="body2" color="primary" fontWeight={600}>
          {jobsCount}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {jobsCount === 1 ? "job" : "jobs"}
        </Typography>
      </Box>
    </Box>
  );
}
